import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users, Mail, Briefcase, Building } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';

interface Person {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  department: string | null;
  created_at: string;
}

export function PeoplePage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);

  async function load() {
    try {
      setPeople(await apiGet<Person[]>('/people'));
    } catch (err) {
      console.error('Failed to load people:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(data: Partial<Person>) {
    await apiPost('/people', data);
    setShowCreate(false);
    load();
  }

  async function handleUpdate(id: string, data: Partial<Person>) {
    await apiPatch(`/people/${id}`, data);
    setEditingPerson(null);
    load();
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this person? They will be unassigned from any tasks.')) return;
    await apiDelete(`/people/${id}`);
    load();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">People</h1>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors">
          <Plus size={16} /> Add Person
        </button>
      </div>

      {people.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <Users size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No people yet</p>
          <p className="text-sm">Add people to assign tasks and @mention them in notes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {people.map(person => (
            <div key={person.id} className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg group">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/20 text-accent flex items-center justify-center text-sm font-semibold">
                  {person.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="text-sm font-medium text-text-primary">{person.name}</span>
                  <div className="flex items-center gap-3 text-xs text-text-muted mt-0.5">
                    {person.email && <span className="flex items-center gap-1"><Mail size={10} />{person.email}</span>}
                    {person.role && <span className="flex items-center gap-1"><Briefcase size={10} />{person.role}</span>}
                    {person.department && <span className="flex items-center gap-1"><Building size={10} />{person.department}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditingPerson(person)} className="p-1 text-text-muted hover:text-text-primary"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(person.id)} className="p-1 text-text-muted hover:text-danger"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreate || editingPerson) && (
        <PersonModal
          person={editingPerson}
          onSave={(data) => editingPerson ? handleUpdate(editingPerson.id, data) : handleCreate(data)}
          onClose={() => { setShowCreate(false); setEditingPerson(null); }}
        />
      )}
    </div>
  );
}

function PersonModal({ person, onSave, onClose }: {
  person: Person | null;
  onSave: (data: Partial<Person>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(person?.name ?? '');
  const [email, setEmail] = useState(person?.email ?? '');
  const [role, setRole] = useState(person?.role ?? '');
  const [department, setDepartment] = useState(person?.department ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">{person ? 'Edit Person' : 'Add Person'}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="Full name"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@company.com"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Role</label>
              <input type="text" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g., Manager"
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <input type="text" value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g., Engineering"
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button onClick={() => name.trim() && onSave({ name: name.trim(), email: email || null, role: role || null, department: department || null })}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50">
            {person ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
